import { Fragment, useCallback, useContext, useMemo } from 'react'

import { toRecord } from '../../lib/helpers'
import { getSanityImageUrl } from '../../lib/image'
import {
  type FrameBuilderExtraService,
  type FrameBuilderSpacerColor,
  type FrameBuilderSpacerSize,
} from '../../lib/state'
import {
  getStateExtraServicePrice,
  getStateExtrasSpacerPrice,
} from '../../lib/state-price'
import { type StepDefinitionProps } from '../../lib/step-definition'
import { FrameBuilderConfigurationContext } from '../../contexts/configuration-context'
import {
  FrameBuilderCommittedStateContext,
  FrameBuilderSelectedStateContext,
} from '../../contexts/state-context'
import { FrameBuilderStringsContext } from '../../contexts/strings-context'

import Button from '../utility/button'
import CheckboxInput from '../utility/checkbox-input'
import ListContainer from '../utility/list-container'
import PortableText from '../utility/portable-text'
import PriceBadge from '../utility/price-badge'
import SmallGraphic from '../utility/small-graphic'
import ConfigurationDefinition from './configuration-definition'

const ExtrasDefinition = ({
  number,
  onClose,
  className,
}: StepDefinitionProps) => {
  const { committedState } = useContext(FrameBuilderCommittedStateContext)
  const { selectedState, dispatchSelectedState } = useContext(
    FrameBuilderSelectedStateContext,
  )
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)
  const configuration = useContext(FrameBuilderConfigurationContext)

  // Calculate spacer prices and extra service prices
  const spacerPrices = useMemo(
    () =>
      toRecord<number>(
        configuration.spacer.sizes.map((size) => ({
          [size._id]: getStateExtrasSpacerPrice(committedState),
        })),
      ),
    [committedState, configuration.spacer.sizes],
  )
  const extraServicePrices = useMemo(
    () =>
      toRecord<number>(
        configuration.extraServices.map((extraService) => ({
          [extraService._id]: getStateExtraServicePrice(
            committedState,
            extraService,
          ),
        })),
      ),
    [committedState, configuration.extraServices],
  )

  const selectExtraService = useCallback(
    (extraService: FrameBuilderExtraService, checked: boolean) => {
      dispatchSelectedState({
        type: 'extras',
        extraService,
        checked,
      })
    },
    [dispatchSelectedState],
  )
  const selectSpacerSize = useCallback(
    (spacerSize: FrameBuilderSpacerSize) => {
      dispatchSelectedState({
        type: 'extras',
        spacerSize,
      })
    },
    [dispatchSelectedState],
  )
  const selectSpacerColor = useCallback(
    (spacerColor: FrameBuilderSpacerColor) => {
      dispatchSelectedState({
        type: 'extras',
        spacerColor,
      })
    },
    [dispatchSelectedState],
  )

  const handleOkClick = useCallback(() => {
    onClose()
  }, [onClose])

  return (
    <ConfigurationDefinition
      number={number}
      onClose={onClose}
      title={frameBuilderStrings.extrasStepTitle}
      className={className}
    >
      {configuration.extraServices.map((extraService) => (
        <Fragment key={extraService._id}>
          <div className="flex flex-col px-6 space-y-6">
            <div className="flex flex-col">
              {extraService.title && (
                <span className="text-2xl">
                  {extraService.title}
                  <PriceBadge
                    price={extraServicePrices[extraService._id]}
                    showPlusSign={true}
                    className="ml-1"
                  />
                </span>
              )}

              <CheckboxInput
                name="glue"
                checked={selectedState.extras.extraServices?.some(
                  (selectedStateExtraService) =>
                    selectedStateExtraService._id === extraService._id,
                )}
                onChange={(checked) =>
                  selectExtraService(extraService, checked)
                }
                label={extraService.label}
                className="mt-2"
              />

              {extraService.description && (
                <div className="mt-4">
                  <PortableText content={extraService.description} />
                </div>
              )}
            </div>
          </div>

          <hr className="bg-gray-300" />
        </Fragment>
      ))}

      <div className="flex flex-col px-6 space-y-6">
        <div className="flex flex-col">
          {frameBuilderStrings.extrasSpacerTitle && (
            <span className="text-2xl">
              {frameBuilderStrings.extrasSpacerTitle}
              {selectedState.extras.spacer.size && (
                <PriceBadge
                  price={spacerPrices[selectedState.extras.spacer.size._id]}
                  showPlusSign={true}
                  className="ml-1"
                />
              )}
            </span>
          )}

          <div className="text-sm leading-5 mt-2">
            {frameBuilderStrings.extrasSpacerSizeLabel}
          </div>
          <ListContainer>
            {configuration.spacer.sizes.map((size) => (
              <SmallGraphic
                key={size._id}
                backgroundImage={getSanityImageUrl(size.icon, {
                  width: 42,
                  height: 45,
                })}
                selected={size._id === selectedState.extras.spacer.size?._id}
                onClick={() => selectSpacerSize(size)}
              />
            ))}
          </ListContainer>

          <div className="text-sm leading-5 mt-2">
            {frameBuilderStrings.extrasSpacerColorLabel}
          </div>
          <ListContainer>
            {configuration.spacer.colors.map((color) => (
              <SmallGraphic
                key={color._id}
                backgroundColor={color.color?.hex}
                selected={color._id === selectedState.extras.spacer.color?._id}
                onClick={() => selectSpacerColor(color)}
              />
            ))}
          </ListContainer>

          {frameBuilderStrings.extrasSpacerDescription && (
            <div className="mt-4">
              <PortableText
                content={frameBuilderStrings.extrasSpacerDescription}
              />
            </div>
          )}
        </div>

        <div className="flex justify-between mt-6">
          <Button
            size="small"
            isVisuallyDisabled={false}
            onClick={handleOkClick}
            className="w-[49%] grow-0 shrink-0"
          >
            {frameBuilderStrings.controlOkLabel}
          </Button>
        </div>
      </div>

      {frameBuilderStrings.extrasStepFooter && (
        <>
          <hr className="bg-gray-300" />
          <div className="flex flex-col px-6 space-y-6">
            <PortableText content={frameBuilderStrings.extrasStepFooter} />
          </div>
        </>
      )}
    </ConfigurationDefinition>
  )
}

export default ExtrasDefinition
